<template>
  <div class="media-analysis-table">
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    
    <div v-else>
      <!-- 統計サマリー -->
      <div class="summary-stats">
        <div class="stat-card">
          <div class="stat-label">総応募数</div>
          <div class="stat-value">{{ totalStats.applications }}人</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">総面接実施数</div>
          <div class="stat-value">{{ totalStats.interviews }}人</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">総採用数</div>
          <div class="stat-value">{{ totalStats.hired }}人</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">平均採用率</div>
          <div class="stat-value">{{ totalStats.hiredRate }}%</div>
        </div>
      </div>

      <!-- 媒体別テーブル -->
      <table class="analysis-table">
        <thead>
          <tr>
            <th rowspan="2">プロセス</th>
            <th v-for="media in data" :key="media.media_type" colspan="2">
              {{ media.media_name }}
            </th>
            <th colspan="2">合計</th>
          </tr>
          <tr>
            <template v-for="media in data">
              <th :key="`${media.media_type}-people`">人数</th>
              <th :key="`${media.media_type}-rate`">率</th>
            </template>
            <th>人数</th>
            <th>率</th>
          </tr>
        </thead>
        <tbody>
          <!-- 応募数行 -->
          <tr>
            <td class="process-name">応募数</td>
            <template v-for="media in data">
              <td :key="`${media.media_type}-app-count`" class="number-cell">{{ media.application_count }}人</td>
              <td :key="`${media.media_type}-app-rate`" class="rate-cell">-</td>
            </template>
            <td class="number-cell total">{{ totalStats.applications }}人</td>
            <td class="rate-cell">-</td>
          </tr>
          
          <!-- 面接実施行 -->
          <tr>
            <td class="process-name">→面接実施</td>
            <template v-for="media in data">
              <td :key="`${media.media_type}-int-count`" class="number-cell">{{ media.interview_count }}人</td>
              <td :key="`${media.media_type}-int-rate`" class="rate-cell">{{ getInterviewRate(media) }}%</td>
            </template>
            <td class="number-cell total">{{ totalStats.interviews }}人</td>
            <td class="rate-cell">{{ totalStats.interviewRate }}%</td>
          </tr>
          
          <!-- 採用行 -->
          <tr>
            <td class="process-name">→採用</td>
            <template v-for="media in data">
              <td :key="`${media.media_type}-hire-count`" class="number-cell">{{ media.hired_count }}人</td>
              <td :key="`${media.media_type}-hire-rate`" class="rate-cell">{{ getHiredRate(media) }}%</td>
            </template>
            <td class="number-cell total">{{ totalStats.hired }}人</td>
            <td class="rate-cell">{{ totalStats.hiredRate }}%</td>
          </tr>
        </tbody>
      </table>

      <!-- 最大離脱ポイント -->
      <div class="dropout-analysis">
        <h3>最大離脱ポイント分析</h3>
        <div class="dropout-cards">
          <div v-for="media in data" :key="`dropout-${media.media_type}`" class="dropout-card">
            <div class="media-name">{{ media.media_name }}</div>
            <div class="dropout-stage">{{ media.max_dropout.stage }}</div>
            <div class="dropout-count">{{ media.max_dropout.count }}人離脱</div>
          </div>
        </div>
      </div>

      <!-- ウォーターフォールチャート -->
      <MediaWaterfallChart :data="data" />
    </div>
  </div>
</template>

<script>
import MediaWaterfallChart from './MediaWaterfallChart.vue'

export default {
  name: 'MediaAnalysisTable',
  components: {
    MediaWaterfallChart
  },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    totalStats() {
      const applications = this.data.reduce((sum, media) => sum + media.application_count, 0)
      const interviews = this.data.reduce((sum, media) => sum + media.interview_count, 0)
      const hired = this.data.reduce((sum, media) => sum + media.hired_count, 0)
      
      return {
        applications,
        interviews,
        hired,
        interviewRate: applications > 0 ? ((interviews / applications) * 100).toFixed(1) : '0.0',
        hiredRate: applications > 0 ? ((hired / applications) * 100).toFixed(1) : '0.0'
      }
    }
  },
  methods: {
    getInterviewRate(media) {
      if (media.application_count === 0) return '0.0'
      return ((media.interview_count / media.application_count) * 100).toFixed(1)
    },
    
    getHiredRate(media) {
      if (media.application_count === 0) return '0.0'
      return ((media.hired_count / media.application_count) * 100).toFixed(1)
    }
  }
}
</script>

<style scoped>
.media-analysis-table {
  width: 100%;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 30px;
}

.stat-card {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.analysis-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  margin-bottom: 30px;
}

.analysis-table th,
.analysis-table td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: center;
}

.analysis-table thead {
  background-color: #f8f9fa;
}

.analysis-table th {
  font-weight: bold;
  color: #333;
}

.process-name {
  text-align: left;
  font-weight: 500;
  background-color: #f8f9fa;
}

.number-cell {
  text-align: right;
}

.rate-cell {
  text-align: right;
  color: #666;
}

.total {
  font-weight: bold;
  background-color: #f0f0f0;
}

.dropout-analysis {
  margin-bottom: 30px;
}

.dropout-analysis h3 {
  font-size: 18px;
  margin-bottom: 16px;
  color: #333;
}

.dropout-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
}

.dropout-card {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.media-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.dropout-stage {
  color: #856404;
  margin-bottom: 4px;
}

.dropout-count {
  font-size: 18px;
  font-weight: bold;
  color: #d39e00;
}


.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/MediaAnalysis/MediaAnalysisTable.vue